import { RegulatoryQuestion, RegulatoryQuestions } from '../interfaces';
import { RegulatoryQuestionModel } from './regulatory-question.model';

export class RegulatoryQuestionsModel implements RegulatoryQuestions {
  readonly regulatoryQuestions: RegulatoryQuestion[];
  readonly rsHandlingQuestions: RegulatoryQuestion[];

  // Static default question configs
  static readonly RCRA1_DEFAULT_CONFIG: RegulatoryQuestion = {
    id: 'rcra1',
    questionText:
      'Does this waste or generating process contain regulated concentrations of the following Pesticides and/ or Herbicides: Chlordane, Endrin, Heptachlor (and its epoxides), Lindane, Methoxychlor, Toxaphene, 2,4-D, or 2,4,5-TP Silvex as defined in 40 CFR 261.33?',
    response: null,
  };
  static readonly RCRA2_DEFAULT_CONFIG: RegulatoryQuestion = {
    id: 'rcra2',
    questionText:
      'Does this waste contain reactive sulfides (greater than 500 ppm) or reactive cyanide (greater than 250  [reference 40 CFR 261.23(a)(5)]?',
    response: null,
  };
  static readonly RCRA3_DEFAULT_CONFIG: RegulatoryQuestion = {
    id: 'rcra3',
    questionText:
      'Does this waste contain regulated concentrations of Polychlorinated Biphenyls (PCBs) as defined in 40 CFR Part 761?',
    response: null,
  };
  static readonly RCRA4_DEFAULT_CONFIG: RegulatoryQuestion = {
    id: 'rcra4',
    questionText:
      'Does this waste contain concentrations of listed hazardous wastes defined in 40 CFR 261.31, 261.32, 261.33, including RCRA F-Listed Solvents?',
    response: null,
  };
  static readonly RCRA5_DEFAULT_CONFIG: RegulatoryQuestion = {
    id: 'rcra5',
    questionText:
      'Has this waste been delisted under 40 CFR 260.20 and 260.22? If yes, attach the final decision to delist the waste as published in the Federal Register.',
    response: null,
    alreadyUploaded: null,
    attachment: null,
    type: 'delisted',
  };
  static readonly RCRA6_DEFAULT_CONFIG: RegulatoryQuestion = {
    id: 'rcra6',
    questionText:
      'Does this waste exhibit a Hazardous Characteristic as defined by Federal and/or State regulations?',
    response: null,
    applicableWasteCodesAndAgents: '',
    type: 'hazardousCharacteristics',
  };
  static readonly RCRA7_DEFAULT_CONFIG: RegulatoryQuestion = {
    id: 'rcra7',
    questionText:
      'Does this waste contain regulated concentrations of 2,3,7,8-Tetrachlorodibenzodioxin (2,3,7,8-TCCD), or any other dioxin as defined in 40 CFR 261.31?',
    response: null,
  };
  static readonly RCRA8_DEFAULT_CONFIG: RegulatoryQuestion = {
    id: 'rcra8',
    questionText:
      'Is this a regulated Medical or Infectious Waste as defined by Federal and/or State regulations?',
    response: null,
  };
  static readonly RCRA9_DEFAULT_CONFIG: RegulatoryQuestion = {
    id: 'rcra9',
    questionText:
      'Is this a regulated Radioactive Waste as defined by Federal and/or State regulations?',
    response: null,
  };
  static readonly RCRA10_DEFAULT_CONFIG: RegulatoryQuestion = {
    id: 'rcra10',
    questionText:
      'Is this a solid waste that is not a hazardous waste in accordance with 40 CFR 261.4(b)? If yes, please provide the corresponding regulatory citation.',
    response: null,
    regulatorCitation: '',
    type: 'hazardousRegulatoryCitation',
  };
  static readonly REP1_DEFAULT_CONFIG: RegulatoryQuestion = {
    id: 'rep1',
    questionText:
      'Does this waste generate heat or react when contacted with water/moisture?',
    response: null,
  };
  static readonly REP2_DEFAULT_CONFIG: RegulatoryQuestion = {
    id: 'rep2',
    questionText: 'Does the waste contain sulfur or sulfur by-products?',
    response: null,
  };
  static readonly REP3_DEFAULT_CONFIG: RegulatoryQuestion = {
    id: 'rep3',
    questionText:
      'Is this waste generated at a State or Federal Superfund cleanup site subject to regulation under CERCLA?',
    response: null,
  };
  static readonly REP4_DEFAULT_CONFIG: RegulatoryQuestion = {
    id: 'rep4',
    questionText:
      'Is this waste from a TSD facility, TSD-like facility or consolidator (i.e. multiple wastes/multiple generators)? . If yes, please provide clarification.',
    response: null,
    description: '',
    attachment: null,
    type: 'tsdLike',
  };

  // Getters return fresh copies to prevent mutation of static data
  static getDefaultRegulatoryQuestions(): RegulatoryQuestion[] {
    return [
      { ...this.RCRA1_DEFAULT_CONFIG },
      { ...this.RCRA2_DEFAULT_CONFIG },
      { ...this.RCRA3_DEFAULT_CONFIG },
      { ...this.RCRA4_DEFAULT_CONFIG },
      { ...this.RCRA5_DEFAULT_CONFIG },
      { ...this.RCRA6_DEFAULT_CONFIG },
      { ...this.RCRA7_DEFAULT_CONFIG },
      { ...this.RCRA8_DEFAULT_CONFIG },
      { ...this.RCRA9_DEFAULT_CONFIG },
      { ...this.RCRA10_DEFAULT_CONFIG },
    ];
  }

  static getDefaultRsHandlingQuestions(): RegulatoryQuestion[] {
    return [
      { ...this.REP1_DEFAULT_CONFIG },
      { ...this.REP2_DEFAULT_CONFIG },
      { ...this.REP3_DEFAULT_CONFIG },
      { ...this.REP4_DEFAULT_CONFIG },
    ];
  }

  static get DEFAULT_CONFIG(): RegulatoryQuestions {
    return {
      regulatoryQuestions: this.getDefaultRegulatoryQuestions(),
      rsHandlingQuestions: this.getDefaultRsHandlingQuestions(),
    };
  }

  constructor(config?: Partial<RegulatoryQuestions>) {
    const regulatory = config?.regulatoryQuestions?.length
      ? config.regulatoryQuestions
      : RegulatoryQuestionsModel.getDefaultRegulatoryQuestions();

    const rsHandling = config?.rsHandlingQuestions?.length
      ? config.rsHandlingQuestions
      : RegulatoryQuestionsModel.getDefaultRsHandlingQuestions();

    this.regulatoryQuestions = regulatory
      .filter(Boolean)
      .map((q) => new RegulatoryQuestionModel(q));

    this.rsHandlingQuestions = rsHandling
      .filter(Boolean)
      .map((q) => new RegulatoryQuestionModel(q));
  }
}
